import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { ThemedSearchableSelect } from '../../themed';

/**
 * AdminUserSelectionSection Component
 *
 * User selection interface for admin bookings.
 * Allows admins to search users (type-to-filter), select a user, and bypass advance booking restrictions.
 *
 * State Ownership:
 * - Reads all props from parent (no local state)
 * - All state managed by useAdminUserSelection hook in parent
 *
 * @param {Object} props
 * @param {Array} props.users - List of users from search
 * @param {boolean} props.usersLoading - Loading state for users
 * @param {string|null} props.selectedUserId - Currently selected user ID
 * @param {boolean} props.bypassAdvanceHours - Whether to bypass advance booking restriction
 * @param {Function} props.onUserChange - Handler for user selection change
 * @param {Function} props.onBypassChange - Handler for bypass checkbox change
 */
const AdminUserSelectionSection = ({
  users,
  usersLoading,
  selectedUserId,
  bypassAdvanceHours,
  onUserChange,
  onBypassChange
}) => {
  const { t } = useTranslation();
  const { text, cn } = useTheme();

  /**
   * Transform users array into react-select format
   * Each option contains value, label, and metadata for rendering and searching
   */
  const userOptions = users.map(user => ({
    value: user.id,
    label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || t('guestAccount'),
    sublabel: user.email || user.phone || t('noContactInfo'),
    isGuest: user.isGuest,
    // Additional fields for custom search filtering
    username: user.username || '',
    email: user.email || '',
    phone: user.phone || '',
    firstName: user.firstName || '',
    lastName: user.lastName || ''
  }));

  /**
   * Custom option renderer to display user details
   * Shows user name on first line, contact info on second line
   * Note: Colors are handled by react-select's option styles, not Tailwind classes
   */
  const formatOptionLabel = ({ label, sublabel, isGuest }) => (
    <div>
      <div className="font-medium">
        {label}
        {isGuest && <span className="text-sm font-normal ml-1">({t('guest')})</span>}
      </div>
      <div className="text-sm opacity-70">
        {sublabel}
      </div>
    </div>
  );

  /**
   * Custom filter function to search across multiple fields
   * Searches: username, first name, last name, email, and phone number
   * Case-insensitive search
   */
  const customFilterOption = (option, searchText) => {
    if (!searchText) return true; // Show all options when no search text

    const search = searchText.toLowerCase();
    const { username, firstName, lastName, email, phone } = option.data;

    // Search across all relevant fields
    return (
      username.toLowerCase().includes(search) ||
      firstName.toLowerCase().includes(search) ||
      lastName.toLowerCase().includes(search) ||
      email.toLowerCase().includes(search) ||
      phone.toLowerCase().includes(search)
    );
  };

  // Find the currently selected option object for react-select
  const selectedOption = userOptions.find(opt => opt.value === selectedUserId) || null;

  return (
    <div className="mb-6 space-y-4">
      {/* Searchable User Dropdown - Type to filter users */}
      <ThemedSearchableSelect
        label={t('selectUser')}
        options={userOptions}
        value={selectedOption}
        onChange={(option) => onUserChange({ target: { value: option?.value || '' } })}
        placeholder={t('searchUsers')}
        isLoading={usersLoading}
        formatOptionLabel={formatOptionLabel}
        filterOption={customFilterOption}
        noOptionsMessage={() => t('noUsersFound')}
        helperText={!selectedUserId ? t('selectUserHelperText') : undefined}
        isClearable={true}
      />

      {/* Bypass Advance Booking Restriction Checkbox */}
      <div className="flex flex-col gap-2">
        <label className={cn("inline-flex items-center text-sm", text.primary)}>
          <input
            type="checkbox"
            className="mr-2"
            checked={bypassAdvanceHours}
            onChange={(e) => onBypassChange(e.target.checked)}
          />
          {t('bypassAdvanceBookingRestriction')}
        </label>
        <p className={cn("text-xs", text.secondary)}>
          {t('bypassAdvanceBookingRestrictionHelper')}
        </p>
      </div>
    </div>
  );
};


export default React.memo(AdminUserSelectionSection);
